import { useEffect, useState } from 'react';
import type { LienFormulaire } from '@rh/shared';
import { appel } from '../api';
import { CandidatureWhatsapp } from './CandidatureWhatsapp';
import { CarteLienFormulaire } from './LienFormulaire';

/**
 * Section « Formulaire » de la fiche annonce (docs/FORMULAIRE_PUBLIC.md § 8).
 *
 * Toute annonce peut porter un lien, pas seulement les annonces « RS » : une
 * annonce déjà publiée sur Keejob ou Tunisie Travail peut recevoir en plus des
 * candidatures des réseaux sociaux, dans la même liste.
 *
 * Lecture ouverte à tous les rôles, comme la fiche qui la porte ; créer,
 * désactiver et régénérer demandent le rôle `rh`.
 */
export function SectionFormulaire({
  annonceId,
  rh,
  ouverte,
}: {
  annonceId: string;
  rh: boolean;
  /** Une annonce close ou archivée ne reçoit plus : on n'y propose pas de lien. */
  ouverte: boolean;
}) {
  const [liens, setLiens] = useState<LienFormulaire[] | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    appel<LienFormulaire[]>(`/annonces/${annonceId}/liens-formulaire`)
      .then((liste) => vivant && setLiens(liste))
      .catch(() => vivant && setLiens([]));
    return () => {
      vivant = false;
    };
  }, [annonceId]);

  async function creer(): Promise<void> {
    setEnCours(true);
    setErreur(null);
    try {
      const lien = await appel<LienFormulaire>(`/annonces/${annonceId}/liens-formulaire`, {
        method: 'POST',
      });
      remplacer(lien);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création impossible');
    } finally {
      setEnCours(false);
    }
  }

  /** Un lien régénéré est neuf : l'ancien reste dans la liste, désactivé. */
  function remplacer(lien: LienFormulaire): void {
    setLiens((actuels) => {
      const autres = (actuels ?? []).filter((l) => l.id !== lien.id).map((l) => ({ ...l, actif: false }));
      return [lien, ...autres].sort((a, b) => Number(b.actif) - Number(a.actif));
    });
  }

  if (!liens) return null;

  const actif = liens.find((l) => l.actif);
  const anciens = liens.filter((l) => !l.actif);

  return (
    <section className="section-formulaire">
      <h3>Formulaire de candidature</h3>

      {actif ? (
        <CarteLienFormulaire lien={actif} rh={rh} surChangement={remplacer} />
      ) : (
        <>
          <p className="aide">
            Un lien à coller sur Facebook, dans un groupe ou sur une affiche : les candidatures
            arrivent dans cette liste, comme celles des plateformes.
          </p>
          {rh && ouverte && (
            <button type="button" className="bouton" disabled={enCours} onClick={() => void creer()}>
              {enCours ? 'Création…' : 'Ajouter un lien de formulaire'}
            </button>
          )}
          {!ouverte && <p className="aide">Cette annonce ne reçoit plus de candidatures.</p>}
        </>
      )}

      {erreur && <p className="erreur">{erreur}</p>}

      {/* La réception WhatsApp est manuelle dans ce lot : le message arrive sur
          le téléphone de l'équipe, la saisie se fait ici. */}
      {rh && ouverte && (
        <div className="actions actions-whatsapp">
          <CandidatureWhatsapp annonceId={annonceId} />
        </div>
      )}

      {anciens.length > 0 && (
        <details className="anciens-liens">
          <summary>Liens désactivés ({anciens.length})</summary>
          {anciens.map((lien) => (
            <CarteLienFormulaire key={lien.id} lien={lien} rh={rh} surChangement={remplacer} />
          ))}
        </details>
      )}
    </section>
  );
}
